import { QuestionCircleOutlined } from '@ant-design/icons'
import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Button, Col, Collapse, Modal, Row, Table, Tooltip, Typography as Text } from 'antd'
import moment from 'moment'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'

import Card from '@/components/Card'
import DurationFilterButton from '@/components/DurationFilterButton'
import FilterDialog from '@/components/FilterDialog'
import LoadingIndicator from '@/components/LoadingIndicator'
import ReportActionButtons from '@/components/ReportActionButtons'
import ReportPrintContainer from '@/components/ReportPrintContainer'
import ReportResultCounts from '@/components/ReportResultCounts'
import config from '@/config'
import { pageStyle } from '@/constants'
import dimorderApi from '@/libs/api/dimorderApi'
import { currencyWithCommas } from '@/libs/numberWithCommas'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

export default function ItemVoidReport(props) {
  const { t, i18n } = useTranslation()
  const { Panel } = Collapse
  const printRef = useRef()
  const classes = useStyles(props)
  const receiptUrl = config.receipt
  const [params, setParams] = React.useState(null)
  const [itemVoid, setItemVoid] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [activeKey, setActiveKey] = React.useState('1')
  const [tableFilters, setTableFilters] = React.useState({})
  const [numberOfResults, setNumberOfResults] = React.useState(0)
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const itemFilters = []
  const itemResults = []
  const staffFilters = []
  const staffResults = []
  const approverFilters = []
  const approverResults = []
  const reasonFilters = []
  const reasonResults = []
  const lang = useSelector((state) => state.app.lang)

  React.useEffect(() => {
    if (params) {
      const getItems = async () => {
        setLoading(true)
        try {
          const itemVoid = await dimorderApi
            .instance(activeMerchant.id)
            ?.getVoidReport(params, 'items')
          setItemVoid(itemVoid)
          setActiveKey('0')
        } catch (error) {
          console.log(error)
          Modal.error({
            title: t('app.common.error'),
            content: t('app.common.submitError'),
          })
          setLoading(false)
          setActiveKey('1')
        }
        setLoading(false)
      }
      getItems()
    }
  }, [params, t, activeMerchant, lang])

  React.useEffect(() => {
    setNumberOfResults(itemVoid?.data?.length)
  }, [itemVoid])

  itemVoid?.data?.forEach((o) => {
    if (o.cancelReason) {
      if (!reasonResults.includes(o.cancelReason)) {
        reasonResults.push(o.cancelReason)
        reasonFilters.push({
          text: o.cancelReason,
          value: o.cancelReason,
        })
      }
    }
    if (o.cancelIdentifier) {
      if (!staffResults.includes(o.cancelIdentifier)) {
        staffResults.push(o.cancelIdentifier)
        staffFilters.push({
          text: o.cancelIdentifier,
          value: o.cancelIdentifier,
        })
      }
    }
    if (o.cancelApprover) {
      if (!staffResults.includes(o.cancelApprover)) {
        approverResults.push(o.cancelApprover)
        approverFilters.push({
          text: o.cancelApprover,
          value: o.cancelApprover,
        })
      }
    }
    if (o.itemName && o.itemId) {
      if (!itemResults.includes(o.itemName)) {
        itemResults.push(o.itemName)
        itemFilters.push({
          text: o.itemName,
          value: o.itemName,
        })
      }
    }
  })

  const voidItemData = {
    columns: [
      {
        title: (
          <Text style={{ display: 'flex', alignItems: 'center' }}>
            {t('app.constant.order.orderNumber')}
            <Tooltip title={t('page.itemVoidReport.orderNoTooltip')}>
              <QuestionCircleOutlined
                style={{
                  paddingLeft: 8,
                  fontSize: 18,
                }}
              />
            </Tooltip>
          </Text>
        ),
        dataIndex: 'order',
        sorter: (a, b) =>
          parseInt(a.order.orderNumber.replace('-', '')) -
          parseInt(b.order.orderNumber.replace('-', '')),
        render: (order) => {
          if (order.deliveryType === t('app.constant.deliveryType.table')) {
            return (
              <Button
                className={classes.btn}
                onClick={() => {
                  window.open(receiptUrl + `${order.orderId}?lang=${i18n.language}`)
                }}
              >
                {order.orderNumber}
              </Button>
            )
          } else {
            return <Text>{order.orderNumber}</Text>
          }
        },
      },
      {
        title: t('app.constant.order.orderType'),
        dataIndex: 'deliveryType',
        filters: [
          {
            text: lang === 'th-TH' ? 'ทานที่ร้าน' : lang === 'en-US' ? 'Dine-in' : '堂食',
            value: lang === 'th-TH' ? 'ทานที่ร้าน' : lang === 'en-US' ? 'Dine-in' : '堂食',
          },
          {
            text: lang === 'th-TH' ? 'สั่งกลับบ้าน' : lang === 'en-US' ? 'Takeaway' : '餐廳外賣',
            value: lang === 'th-TH' ? 'สั่งกลับบ้าน' : lang === 'en-US' ? 'Takeaway' : '餐廳外賣',
          },
          {
            text: lang === 'th-TH' ? 'จัดส่ง' : lang === 'en-US' ? 'Delivery' : '點單外送',
            value: lang === 'th-TH' ? 'จัดส่ง' : lang === 'en-US' ? 'Delivery' : '點單外送',
          },
          {
            text:
              lang === 'th-TH'
                ? 'สั่งล่วงหน้า'
                : lang === 'en-US'
                ? 'DimOrder Takeaway'
                : '點單外賣',
            value:
              lang === 'th-TH'
                ? 'สั่งล่วงหน้า'
                : lang === 'en-US'
                ? 'DimOrder Takeaway'
                : '點單外賣',
          },
        ],
        filteredValue: tableFilters?.deliveryType || null,
        onFilter: (value, record) => record?.deliveryType === value,
      },
      {
        title: t('page.itemVoidReport.createAt'),
        dataIndex: 'createdAt',
        sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      },
      {
        title: t('page.itemVoidReport.cancelledItem'),
        dataIndex: 'cancelledItem',
        filters: itemFilters,
        filteredValue: tableFilters?.cancelledItem || null,
        onFilter: (value, record) => record?.cancelledItem === value,
      },
      {
        title: t('page.itemVoidReport.cancelledAt'),
        dataIndex: 'cancelledAt',
        sorter: (a, b) => new Date(a.cancelledAt) - new Date(b.cancelledAt),
      },
      {
        title: t('page.itemVoidReport.cancelledBy'),
        dataIndex: 'cancelIdentifier',
        filters: staffFilters,
        filteredValue: tableFilters?.cancelIdentifier || null,
        onFilter: (value, record) => record?.cancelIdentifier === value,
      },
      {
        title: t('page.itemVoidReport.approveBy'),
        dataIndex: 'cancelApprover',
        filters: approverFilters,
        filteredValue: tableFilters?.cancelApprover || null,
        onFilter: (value, record) => record?.cancelApprover === value,
      },
      {
        title: t('page.itemVoidReport.cancelReason'),
        dataIndex: 'cancelReason',
        filters: reasonFilters,
        filteredValue: tableFilters?.cancelReason || null,
        onFilter: (value, record) => record?.cancelReason === value,
      },
      {
        title: (
          <Text style={{ display: 'flex', alignItems: 'center' }}>
            {t('page.itemVoidReport.price')}
            <Tooltip title={t('page.itemVoidReport.priceDesc')}>
              <QuestionCircleOutlined
                style={{
                  paddingLeft: 8,
                  fontSize: 18,
                }}
              />
            </Tooltip>
          </Text>
        ),
        dataIndex: 'price',
        render: (price) => (
          <Text>{currencyWithCommas(price, activeMerchant?.setting?.currency, activeMerchant?.rounding?.minFractionDigits)}</Text>
        ),
        sorter: (a, b) => a.price - b.price,
      },
    ],
    data: [],
  }

  if (itemVoid?.data) {
    for (const [key, value] of Object.entries(itemVoid?.data)) {
      voidItemData.data.push({
        key,
        order: {
          orderNumber: value.orderNumber,
          orderId: value.orderId,
          deliveryType: value.deliveryType,
        },
        deliveryType: value.deliveryType,
        createdAt: moment(value.createdAt).format('YYYY-MM-DD HH:mm'),
        cancelledItem: value.itemName,
        cancelledAt: moment(value.cancelledAt).format('YYYY-MM-DD HH:mm'),
        cancelIdentifier: value.cancelIdentifier || '--',
        cancelApprover: value.cancelApprover || '--',
        cancelReason: value.cancelReason,
        price: value.amount,
      })
    }
  }

  const handleTableChange = (pagination, filters, sorter, extra) => {
    setTableFilters(filters)
    setNumberOfResults(extra.currentDataSource.length)
  }

  const displayResult = () => (
    <Row gutter={16}>
      <Col span={24}>
        <Card
          width='100%'
          height='100%'
          padding='20px'
          borderRadius='2px'
          textAlign='left'
          boxShadow='1px 1px  5px rgba(0, 0, 0, 0.3)'
        >
          <ReportResultCounts counts={numberOfResults} />
          <br />
          <Table
            columns={voidItemData.columns}
            dataSource={voidItemData.data}
            scroll={{ x: 500 }}
            onChange={handleTableChange}
          />
        </Card>
      </Col>
    </Row>
  )

  const printResult = () => (
    <ReportPrintContainer
      printRef={printRef}
      start={params?.start}
      end={params?.end}
      title={t('page.itemVoidReport.title')}
    >
      <Row gutter={16}>
        <Col span={24}>
          <ReportResultCounts counts={numberOfResults} />
          <br />
          <Table
            columns={voidItemData.columns}
            dataSource={voidItemData.data}
            pagination={false}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
    </ReportPrintContainer>
  )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle,
  })

  const handleExport = async () => {
    try {
      await dimorderApi.instance(activeMerchant.id)?.exportVoidReportCsv(params, 'items')
    } catch (error) {
      console.log(error)
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.exportError'),
      })
    }
  }

  return (
    <PageContainer
      ghost
      header={{
        title: (
          <Text style={{ display: 'flex', alignItems: 'center' }}>
            {t('page.itemVoidReport.title')}
            <Tooltip title={t('page.itemVoidReport.titleDesc')}>
              <QuestionCircleOutlined
                style={{
                  paddingLeft: 8,
                  fontSize: 18,
                }}
              />
            </Tooltip>
          </Text>
        ),
      }}
    >
      <Collapse
        ghost
        expandIcon={() => <></>}
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
      >
        <Panel
          key='1'
          style={{ fontSize: 22 }}
          extra={
            <DurationFilterButton start={params?.start} end={params?.end} activeKey={activeKey} />
          }
        >
          <FilterDialog filter filterTime setParams={setParams} />
        </Panel>
      </Collapse>
      <br />
      <LoadingIndicator loading={loading} />
      {itemVoid && !loading && (
        <>
          <ReportActionButtons onPrint={handlePrint} onExport={handleExport} />
          {displayResult()}
          {printResult()}
        </>
      )}
    </PageContainer>
  )
}

const useStyles = makeStyles(() => ({
  btn: {
    borderColor: colors.button,
    borderRadius: 5,
    color: colors.button,
  },
}))
